import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { GREAT_WHITE, EERIE, OLAF } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITable from 'UIComponents/table/UITable';
import { ApiNameToProductTranslationKeyMap } from 'pricing-pages-ui/constants/ApiNameToProductTranslationKeyMap';
import { getFeaturesByProductLineSelector } from '../../selectors/FeatureSelectors';
import FeatureTableRow from './FeatureTableRow';
import FeatureTablePriceDisplay from './FeatureTablePriceDisplay';
import { hasPricingAndPurchasingSucceededSelector, hasProductsSucceededSelector } from '../../selectors/LoadingStateSelectors';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import FeatureTableCol from './FeatureTableCol';
import FeatureTableColTag from './FeatureTableColTag';
import { daysSinceProductStartDateFromProductInventorySelector, highestProductLevelFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
const GradientOverlay = styled.div.withConfig({
  displayName: "FeatureTable__GradientOverlay",
  componentId: "sc-1l3bse2-0"
})(["position:relative;overflow:hidden;max-height:", ";&::after{visibility:", ";opacity:", ";position:absolute;bottom:0;height:100%;width:100%;content:'';background:linear-gradient(to bottom,transparent 70%,", ");pointer-events:none;}"], props => props.expanded ? '100%' : '650px', props => props.expanded ? 'hidden' : 'visible', props => props.expanded ? 0 : 1, OLAF);
const THead = styled.thead.withConfig({
  displayName: "FeatureTable__THead",
  componentId: "sc-1l3bse2-1"
})(["tr{position:relative;}& tr::after{content:'';position:absolute;bottom:0;left:0;right:0;border:1px solid transparent;box-shadow:0 2px 4px ", ";}& tr th{background-color:transparent;border:none;font-size:0.875rem;text-transform:inherit;text-align:center;font-weight:600;white-space:nowrap;padding-top:12px;padding-bottom:8px;position:relative;}& tr th:first-of-type{font-size:1.5rem;text-align:left;line-height:100%;vertical-align:text-top;}"], GREAT_WHITE);
const TBody = styled.tbody.withConfig({
  displayName: "FeatureTable__TBody",
  componentId: "sc-1l3bse2-2"
})(["& tr > *{border:1px solid ", ";}& tr td{text-align:center;}& tr th[scope='row']{text-align:left;padding:16px 24px;}"], GREAT_WHITE);

const FeatureTable = ({
  productLine,
  highlightedProducts,
  screen,
  tab
}) => {
  const [expanded, setExpanded] = useState(false);
  const highlightedProductsSet = new Set(highlightedProducts);
  const features = useSelector(state => {
    return getFeaturesByProductLineSelector(state, {
      productLine
    });
  });
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const hasProductsSucceeded = useSelector(hasProductsSucceededSelector);
  const productLevelOwned = useSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return null;
    return highestProductLevelFromProductInventorySelector(state, {
      productLine
    });
  });
  const numOfDaysOwned = useSelector(state => {
    if (!productLevelOwned) return 0;
    return daysSinceProductStartDateFromProductInventorySelector(state, {
      productAttributeFilters: {
        productLine,
        productLevel: productLevelOwned
      }
    });
  });
  const recommendedProductLevel = PROFESSIONAL;
  const showRecommended = productLevelOwned === STARTER && numOfDaysOwned > 30;

  const handleExpandClick = () => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${expanded ? 'collapse' : 'expand'}-table`,
      productLine,
      products: highlightedProducts,
      currentPlan: productLevelOwned,
      recommendedPlan: recommendedProductLevel,
      showRecommended,
      screen,
      tab
    });
    setExpanded(prev => !prev);
  };

  if (!features.length) return null;
  return /*#__PURE__*/_jsxs("div", {
    className: "p-y-8",
    children: [/*#__PURE__*/_jsx(GradientOverlay, {
      expanded: expanded,
      children: /*#__PURE__*/_jsxs(UITable, {
        bordered: false,
        hover: false,
        className: "m-top-8",
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {}), highlightedProducts.map(apiName => {
            return /*#__PURE__*/_jsx(FeatureTableCol, {
              apiName: apiName,
              productLine: productLine,
              recommendedProductLevel: recommendedProductLevel,
              showRecommended: showRecommended
            }, apiName);
          })]
        }), /*#__PURE__*/_jsx(THead, {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "upgrades.productCatalog.tableHeader.features"
              })
            }), highlightedProducts.map(apiName => {
              const apiNameForPrice = getProductLevelFromApiNameString(apiName) === FREE ? HUBSPOT_FREE : apiName;
              return /*#__PURE__*/_jsxs("th", {
                scope: "col",
                children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
                  apiName: apiName,
                  productLine: productLine,
                  recommendedProductLevel: recommendedProductLevel,
                  showRecommended: showRecommended
                }), /*#__PURE__*/_jsx("div", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: ApiNameToProductTranslationKeyMap[apiName]
                  })
                }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
                  isLoading: !hasPricingAndPurchasingSucceeded || !hasProductsSucceeded,
                  heights: [20],
                  widths: [60],
                  className: "flex-column align-center",
                  Content: FeatureTablePriceDisplay,
                  contentProps: {
                    apiName: apiNameForPrice
                  }
                })]
              }, apiName);
            })]
          })
        }), /*#__PURE__*/_jsx(TBody, {
          children: features.map(feature => {
            return /*#__PURE__*/_jsx(FeatureTableRow, {
              highlightedProducts: highlightedProducts,
              feature: feature,
              productLine: productLine,
              highlightedProductsSet: highlightedProductsSet
            }, feature.featureId);
          })
        })]
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "text-center justify-center align-center",
      children: /*#__PURE__*/_jsxs(UIButton, {
        use: "transparent",
        onClick: handleExpandClick,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: expanded ? 'featureTable.collapse' : 'featureTable.expand'
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: expanded ? 'up' : 'down',
          size: "xxs",
          color: EERIE
        })]
      })
    })]
  });
};

export default FeatureTable;